import { DocFilters, MetadataFieldFilter } from '@/types';

interface DocPagination {
  pageIndex: number;
  pageSize: number;
}

interface DocUrlState {
  filters: DocFilters;
  pagination: DocPagination;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
}

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT_ORDER = 'DESC';

/**
 * Serialize doc state to URL search params.
 * Omits default values to keep URLs clean.
 */
export function serializeDocStateToParams(
  filters: DocFilters,
  pagination: DocPagination,
  sortBy: string,
  sortOrder: 'ASC' | 'DESC',
): URLSearchParams {
  const params = new URLSearchParams();

  // Boolean filters
  if (filters.processed !== undefined && filters.processed !== null) {
    params.set('processed', String(filters.processed));
  }
  if (filters.hasKeyFacts !== undefined && filters.hasKeyFacts !== null) {
    params.set('hasKeyFacts', String(filters.hasKeyFacts));
  }
  if (filters.hasRelevantFacts !== undefined && filters.hasRelevantFacts !== null) {
    params.set('hasRelevantFacts', String(filters.hasRelevantFacts));
  }

  // String filters
  if (filters.searchQuery) {
    params.set('searchQuery', filters.searchQuery);
  }

  // Date filters (ISO string)
  if (filters.startDate) {
    params.set('startDate', filters.startDate);
  }
  if (filters.endDate) {
    params.set('endDate', filters.endDate);
  }

  // String array filters (comma-separated)
  if (filters.extensions && filters.extensions.length > 0) {
    params.set('extensions', filters.extensions.join(','));
  }
  if (filters.documentTypes && filters.documentTypes.length > 0) {
    params.set('documentTypes', filters.documentTypes.join(','));
  }
  if (filters.documentThemes && filters.documentThemes.length > 0) {
    params.set('documentThemes', filters.documentThemes.join(','));
  }
  if (filters.docIds && filters.docIds.length > 0) {
    params.set('docIds', filters.docIds.join(','));
  }
  if (filters.issues && filters.issues.length > 0) {
    params.set('issues', filters.issues.join(','));
  }
  if (filters.personNames && filters.personNames.length > 0) {
    params.set('personNames', filters.personNames.join(','));
  }
  if (filters.runIds && filters.runIds.length > 0) {
    params.set('runIds', filters.runIds.join(','));
  }

  // Metadata filters (JSON)
  if (filters.metadataFilters && filters.metadataFilters.length > 0) {
    params.set('metadataFilters', JSON.stringify(filters.metadataFilters));
  }

  // Pagination (1-indexed for user-friendliness, omit defaults)
  if (pagination.pageIndex > 0) {
    params.set('page', String(pagination.pageIndex + 1));
  }
  if (pagination.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('pageSize', String(pagination.pageSize));
  }

  // Sort (omit defaults)
  if (sortBy) {
    params.set('sortBy', sortBy);
  }
  if (sortOrder !== DEFAULT_SORT_ORDER) {
    params.set('sortOrder', sortOrder);
  }

  return params;
}

/**
 * Parse URL search params to doc state.
 * Returns defaults for missing values.
 */
export function parseDocStateFromParams(searchParams: URLSearchParams): DocUrlState {
  const filters: DocFilters = {};

  // Boolean filters
  const processed = searchParams.get('processed');
  if (processed !== null) {
    filters.processed = processed === 'true';
  }
  const hasKeyFacts = searchParams.get('hasKeyFacts');
  if (hasKeyFacts !== null) {
    filters.hasKeyFacts = hasKeyFacts === 'true';
  }
  const hasRelevantFacts = searchParams.get('hasRelevantFacts');
  if (hasRelevantFacts !== null) {
    filters.hasRelevantFacts = hasRelevantFacts === 'true';
  }

  // String filters
  const searchQuery = searchParams.get('searchQuery');
  if (searchQuery) {
    filters.searchQuery = searchQuery;
  }

  // Date filters
  const startDate = searchParams.get('startDate');
  if (startDate) {
    filters.startDate = startDate;
  }
  const endDate = searchParams.get('endDate');
  if (endDate) {
    filters.endDate = endDate;
  }

  // String array filters
  const extensions = searchParams.get('extensions');
  if (extensions) {
    filters.extensions = extensions.split(',').filter(Boolean);
  }
  const documentTypes = searchParams.get('documentTypes');
  if (documentTypes) {
    filters.documentTypes = documentTypes.split(',').filter(Boolean);
  }
  const documentThemes = searchParams.get('documentThemes');
  if (documentThemes) {
    filters.documentThemes = documentThemes.split(',').filter(Boolean);
  }
  const docIds = searchParams.get('docIds');
  if (docIds) {
    filters.docIds = docIds.split(',').filter(Boolean);
  }
  const issues = searchParams.get('issues');
  if (issues) {
    filters.issues = issues.split(',').filter(Boolean);
  }
  const personNames = searchParams.get('personNames');
  if (personNames) {
    filters.personNames = personNames.split(',').filter(Boolean);
  }
  const runIds = searchParams.get('runIds');
  if (runIds) {
    filters.runIds = runIds.split(',').filter(Boolean);
  }

  // Metadata filters (JSON)
  const metadataFiltersStr = searchParams.get('metadataFilters');
  if (metadataFiltersStr) {
    try {
      const parsed = JSON.parse(metadataFiltersStr) as MetadataFieldFilter[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        filters.metadataFilters = parsed;
      }
    } catch {
      // Gracefully ignore invalid JSON
    }
  }

  // Pagination (1-indexed in URL, 0-indexed internally)
  const page = searchParams.get('page');
  const pageSize = searchParams.get('pageSize');
  const pagination: DocPagination = {
    pageIndex: page ? Math.max(0, parseInt(page, 10) - 1) : 0,
    pageSize: pageSize ? parseInt(pageSize, 10) : DEFAULT_PAGE_SIZE,
  };

  // Sort
  const sortBy = searchParams.get('sortBy') || '';
  const sortOrderParam = searchParams.get('sortOrder');
  const sortOrder: 'ASC' | 'DESC' =
    sortOrderParam === 'ASC' || sortOrderParam === 'DESC' ? sortOrderParam : DEFAULT_SORT_ORDER;

  return { filters, pagination, sortBy, sortOrder };
}
